import { useCallback, useEffect, useState } from "react";
import { CopyURLTypes } from "types";
import classNames from "classnames";

import { fetchDatasets, setDeleteModalId } from "redux/Datasets/actions";
import { useDispatch } from "react-redux";

import { setShowModal } from "redux/Modals/actions";
import LinkIcon from "assets/icons/LinkIcon";
import DeleteIcon from "assets/icons/DeleteIcon";
import DuplicateIcon from "assets/icons/DuplicateIcon";
import { copyToClipboard } from "helpers/utility/utilities";
import { setDatasetIdToDuplicate } from "redux/Datasets/actions";
import { useFeatureFlagService } from "hooks/useFeatureFlagService";
import { ENABLE_DS_DUPLICATE } from "helpers/constants/filters";

import styles from "./style.module.scss";
import { LoadingOutlined, ReloadOutlined } from "@ant-design/icons";
import {
  useGetUserConfigApiV1UserConfigGet,
  useRestoreSnapshotApiV1DatasetDatasetIdSnapshotSnapshotIdRestorePost,
} from "../../../vl-api/vlComponents";
import { App } from "antd";
import { Link } from "react-router-dom";
import { DELETE_DATASET } from "../../../helpers/constants/filters";
import VLTooltip from "../../uikit/VLTooltip";
import { NO_PERMISSION_TOOLTIP_TEXT } from "../../../helpers/constants/tooltips";
import { UserPermission } from "../../../contexts/UserConfigContext";
import { useDeleteDatasetContext } from "../../providers/DeleteDatasetProvider/DeleteDatasetProvider";

const getIsEnabledAndReason = (
  userConfig: any,
  featureKey: string,
  permission: UserPermission,
) => {
  if (!userConfig) {
    return { enabled: false, reason: "" };
  }
  const featureFlag = userConfig.features.find(
    ({ feature_key }: any) => feature_key === featureKey,
  );
  if (!featureFlag || featureFlag.feature_behavior === "HIDE") {
    return { enabled: false, reason: "Action not supported" };
  }
  if (featureFlag.feature_behavior === "GREY_OUT") {
    return {
      enabled: false,
      reason: featureFlag.reason,
    };
  }
  if (!userConfig.permissions?.[permission]) {
    return { enabled: false, reason: NO_PERMISSION_TOOLTIP_TEXT };
  }
  return {
    enabled: true,
    reason: null,
  };
};

const DeleteButton = ({ datasetId, userConfig }: DeleteButtonProps) => {
  // when user open the 3dots menu, load user_config for dataset to see if he can delete (based on FF and permission)
  const { enabled: canDeleteDataset, reason } = getIsEnabledAndReason(
    userConfig,
    DELETE_DATASET,
    "can_delete",
  );

  const { deleteDataset } = useDeleteDatasetContext();

  return (
    <VLTooltip title={reason}>
      <li
        className={classNames(styles.datasetHamburgerRow, {
          [styles.disabled]: !canDeleteDataset,
        })}
        onClick={() => canDeleteDataset && deleteDataset(datasetId)}
      >
        <DeleteIcon />
        <span className={styles.datasetHamburgerRowText}>Delete</span>
        {!userConfig && <LoadingOutlined className={styles.loader} />}
      </li>
    </VLTooltip>
  );
};

const RecoverButton = ({
  datasetId,
  closeModal,
  userConfig,
}: RecoverButtonProps) => {
  const { notification } = App.useApp();
  const dispatch = useDispatch();
  const canRestoreDataset = userConfig?.permissions?.can_restore_snapshot;
  const restoreMutation =
    useRestoreSnapshotApiV1DatasetDatasetIdSnapshotSnapshotIdRestorePost({
      onSuccess: () => {
        dispatch(fetchDatasets() as any);
        notification.success({
          message: (
            <div className={styles.notification}>
              Recover in progress, you can track the progress in the task
              manager.
            </div>
          ),
          description: (
            <Link to="/task-manager" onClick={() => notification.destroy()}>
              View in task manager
            </Link>
          ),
        });
        closeModal();
      },
    });
  const handleClick = () => {
    if (!canRestoreDataset) {
      return;
    }
    restoreMutation.mutate({
      pathParams: { datasetId, snapshotId: "current" },
    });
  };
  return (
    <VLTooltip title={!canRestoreDataset ? NO_PERMISSION_TOOLTIP_TEXT : null}>
      <li
        className={classNames(styles.datasetHamburgerRow, {
          [styles.disabled]: !canRestoreDataset,
        })}
        onClick={handleClick}
      >
        <ReloadOutlined />
        <span className={styles.datasetHamburgerRowText}>Recover</span>
      </li>
      {!userConfig && <LoadingOutlined className={styles.loader} />}
    </VLTooltip>
  );
};

const DuplicateButton = ({ datasetId, closeModal }: DuplicateButtonProps) => {
  const dispatch = useDispatch();
  const handleButtonClick = (event: any) => {
    event.preventDefault();
    event.stopPropagation();
    closeModal();
    dispatch(setDatasetIdToDuplicate(datasetId));
    dispatch(setShowModal(true));
  };
  const showButton = useFeatureFlagService(ENABLE_DS_DUPLICATE).enabled;
  return showButton ? (
    <li className={styles.datasetHamburgerRow} onClick={handleButtonClick}>
      <DuplicateIcon />
      <span className={styles.datasetHamburgerRowText}>Duplicate</span>
    </li>
  ) : null;
};

function DatasetHamburger({
  datasetId,
  deleteModalId,
  url,
  showDeleteButton,
  copyButtonURL,
  showRecoverOption,
}: any) {
  const [isOpen, setIsOpen] = useState(false);
  const dispatch = useDispatch();
  const { message } = App.useApp();

  const { data: userConfig } = useGetUserConfigApiV1UserConfigGet<any>(
    {
      queryParams: { dataset_id: datasetId },
    },
    { enabled: isOpen },
  );

  const copyUrlRowClassnames = classNames(styles.datasetHamburgerRow, {
    [styles.disabledRow]: !copyButtonURL,
  });

  const closeModal = useCallback(() => {
    setIsOpen(false);
    dispatch(setDeleteModalId(null) as any);
  }, [dispatch]);

  // eslint-disable-next-line
  const listener = (event: KeyboardEvent) => {
    if (event.key === "Escape") {
      closeModal();
      document.removeEventListener("keydown", listener);
    }
  };

  const checkIfClickedOutside = useCallback(
    (event: any) => {
      event.stopPropagation();
      const className = (event.target.className as string) || "";
      let flag = className.includes("datasetHamburger");

      if (!flag) {
        closeModal();
      }
    },
    [closeModal],
  );

  const handleCopyButtonClick = (event: any) => {
    let copyURL: CopyURLTypes = copyButtonURL;

    if (!copyURL) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    let urlToCopy = new URL(window.location.href);

    const endpoint = copyURL === "/create" ? "/create" : "/data";

    urlToCopy.pathname = `/dataset/${url}${endpoint}`;
    copyToClipboard(urlToCopy.toString());

    message.success("Copied to clipboard!");

    closeModal();
  };

  useEffect(() => {
    if (deleteModalId === datasetId) {
      setIsOpen(true);
    } else {
      setIsOpen(false);
    }
  }, [deleteModalId, datasetId]);

  useEffect(() => {
    if (isOpen) {
      document.addEventListener("click", checkIfClickedOutside);
    } else {
      document.removeEventListener("click", checkIfClickedOutside);
    }
    return () => {
      document.removeEventListener("click", checkIfClickedOutside);
    };
  }, [checkIfClickedOutside, isOpen]);

  useEffect(() => {
    document.addEventListener("keydown", listener);
    return () => {
      document.removeEventListener("keydown", listener);
    };
    // eslint-disable-next-line
  }, [listener]);

  return isOpen ? (
    <div className={styles.datasetHamburgerMainContainer}>
      <div className={styles.datasetHamburgerInnerContainer}>
        <ul className={styles.datasetHamburgerListContainer}>
          {copyButtonURL && (
            <li
              className={copyUrlRowClassnames}
              onClick={handleCopyButtonClick}
            >
              <LinkIcon />
              <span className={styles.datasetHamburgerRowText}>Copy URL</span>
            </li>
          )}
          {showDeleteButton && (
            <DeleteButton userConfig={userConfig} datasetId={datasetId} />
          )}
          <DuplicateButton datasetId={datasetId} closeModal={closeModal} />
          {showRecoverOption && (
            <RecoverButton
              userConfig={userConfig}
              datasetId={datasetId}
              closeModal={closeModal}
            />
          )}
        </ul>
      </div>
    </div>
  ) : null;
}

export default DatasetHamburger;

interface DeleteButtonProps {
  datasetId: string;
  userConfig: any | undefined;
}

type RecoverButtonProps = DuplicateButtonProps & {
  userConfig: any;
};

interface DuplicateButtonProps {
  datasetId: string;
  closeModal: Function;
}
